import { ActionButton, AddItemButton, Button, Collapse, makeStyles, Tooltip, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { StripeMark } from '@paintscout/ui/icons'
import { DialogStackContext, useClientOptions, useUser } from '@paintscout/ui-business'
import { useFlags } from 'shared/components/common/FlagsProvider'
import SettingsIcon from '@material-ui/icons/SettingsOutlined'
import CheckIcon from '@material-ui/icons/Check'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import DownloadIcon from '@material-ui/icons/GetApp'
import LaunchIcon from '@material-ui/icons/Launch'
import UndoIcon from '@material-ui/icons/Undo'
import SendIcon from '@material-ui/icons/Send'
import SubdirectoryArrowRightIcon from '@material-ui/icons/SubdirectoryArrowRight'
import { canRefundPayment, getPayments, getPaymentStage, requestHasProcessingPayment } from '@paintscout/util/builder'
import { getPaymentPortalLink } from '@paintscout/util/util'
import classnames from 'classnames'
import moment from 'moment'
import type { PayrixPaymentType, QuoteDocument } from 'paintscout'
import React, { useContext } from 'react'
import { useQuote } from '../context'
import { useHasPaymentSettings } from './useHasPaymentSettings'
import { QuotePdfDialog, RefundPaymentDialog, SendPaymentRequestDialog, TileListDialog } from '../dialogs'
import PaymentStatusBadgeWithTooltip from '../Quote/views/QuoteView/PaymentsTable/PaymentStatusBadgeWithTooltip'
import { RECORD_PAYMENT_KEY, usePaymentActions } from '../Quote/views/QuoteView/PaymentsTable/usePaymentActions'

export interface PaymentsTableNextProps {
  inPresentation?: boolean
  collapsed?: boolean
  onChange: (quote: QuoteDocument) => void
  onToggle?: (ev: React.MouseEvent) => any
}

// Amount | Status | Method | Client Note (grows) | Date | kebab — shared by the column header and
// data rows so they line up exactly (order matches the mock). Tracks are content-independent
// (fixed min + fr, no `max-content`): the header band and each data row are separate grids, so a
// content-sized track would resolve to a different width in each and the columns would drift apart.
//
// The minimums are sized to the widest realistic content, because a grid track never shrinks below
// its `minmax()` floor — once a floor is too small the cell's nowrap content spills into the next
// column instead (ENG-1717: at iPad/side-menu-open widths every track sat at its floor, so the
// amount + inline stage ran under the status badge and the date ran under the kebab). Amount holds
// "$12,345.00 Deposit", Status a "Successful"/"Requested" badge, Date "Sep 22, 2026"; Method and
// Client Note are the two that ellipsize cleanly, so they carry the smaller floors and Client Note
// keeps the largest `fr` to absorb spare width. A low floor costs nothing when there is room — the
// `fr` maximums still hand Client Note most of the free space — so the floors stay just wide enough
// for each column's own header label ("Client Note" is the widest at 72px).
const GRID_COLUMNS =
  'minmax(128px, 1fr) minmax(104px, 1fr) minmax(72px, 0.8fr) minmax(72px, 2.4fr) minmax(96px, 1fr) 36px'

// Mobile drops the table shape entirely, per the prototype: no column header, and each payment is a
// two-line block — the amount with the status badge on the right, then "Stage • Date"
// under it in small muted type (ENG-2071). A four-column row squeezed the amount to "$800.0C" and
// cut the date off. The areas are placed by name, so the cells' DOM order doesn't matter.
//
// No kebab on mobile either: tapping the row opens the payment, which is where its actions live
// there (ENG-2071 — the prototype leaves the "…" out on purpose).
const MOBILE_ROW_AREAS = '"amount status" "meta meta"'

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      // Layout wrapper only — stacks the single bordered card above the detached add box with a small
      // gap. No frame of its own, so the card's border is the only one (avoids a double-border look).
      root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5)
      },
      // Inner bordered block holding the title strip + columns + rows. Border matches the shared
      // OutlinedTableCard (0.8px P85) so it doesn't read lighter than the other outlined tables.
      card: {
        border: `0.8px solid ${neutral.P85}`,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: neutral.White,
        overflow: 'hidden',
        '@media print': {
          border: 'none'
        }
      },
      // Title strip: "Payments" + the Settings button. P98 fill to match the other outlined section
      // headers (e.g. the Options solo header).
      titleHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        minHeight: 46,
        padding: theme.spacing(0, 1.5, 0, 2),
        backgroundColor: neutral.P98,
        borderBottom: `1px solid ${neutral.P94}`,
        // The prototype's mobile strip: 40px tall, 16px in, over a 0.8px P85 hairline.
        [theme.breakpoints.down('xs')]: {
          minHeight: 40,
          padding: theme.spacing(0, 2),
          borderBottom: `0.8px solid ${neutral.P85}`
        }
      },
      // When the body below is collapsed/empty, drop the title strip's divider so it doesn't leave a
      // stray border on the card's bottom edge.
      titleHeaderFlush: {
        borderBottom: 'none'
      },
      title: {
        fontWeight: 600,
        fontSize: theme.typography.pxToRem(15),
        color: neutral.P35,
        // The prototype's mobile title: 15px / 500 / #252e3d, darker than any Neutral step we use here.
        [theme.breakpoints.down('xs')]: {
          fontWeight: 500,
          color: '#252e3d'
        }
      },
      // Column-header band: light P98 fill, muted labels, bottom divider.
      colHeader: {
        display: 'grid',
        gridTemplateColumns: GRID_COLUMNS,
        alignItems: 'center',
        columnGap: theme.spacing(2),
        padding: theme.spacing(0.75, 1.5, 0, 2),
        [theme.breakpoints.down('xs')]: {
          display: 'none'
        }
      },
      colLabel: {
        fontSize: theme.typography.pxToRem(13),
        color: neutral.P60,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      dataRow: {
        display: 'grid',
        gridTemplateColumns: GRID_COLUMNS,
        alignItems: 'center',
        columnGap: theme.spacing(2),
        padding: theme.spacing(1, 1.5, 1, 2),
        borderBottom: `1px solid ${neutral.P94}`,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: neutral.P98
        },
        '&:last-child': {
          borderBottom: 'none'
        },
        '&:first-child': {
          paddingTop: theme.spacing(0)
        },
        [theme.breakpoints.down('xs')]: {
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          gridTemplateAreas: MOBILE_ROW_AREAS,
          columnGap: theme.spacing(1),
          rowGap: theme.spacing(1),
          // The prototype's 16px all round; no header above, so the first row keeps its top inset.
          padding: theme.spacing(2),
          '&:first-child': {
            paddingTop: theme.spacing(2)
          }
        }
      },
      cell: {
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P40,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      // Desktop-only cells: Method and Client Note (the prototype's mobile row has neither), plus the
      // Date cell and inline stage, which move into `mobileMeta` there.
      hideOnMobile: {
        [theme.breakpoints.down('xs')]: {
          display: 'none'
        }
      },
      // Mobile's second line: "Deposit • Sep 23, 2026", 12px and muted.
      mobileMeta: {
        display: 'none',
        [theme.breakpoints.down('xs')]: {
          gridArea: 'meta',
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1),
          minWidth: 0,
          fontSize: theme.typography.pxToRem(12),
          color: neutral.P60,
          whiteSpace: 'nowrap',
          overflow: 'hidden'
        }
      },
      // The status badge on mobile, drawn to the prototype's pill: 24px tall, 12px left / 8px right,
      // 6px corners and a 0.8px border. The badge is shared with the desktop table and the classic
      // editor, so it is resized here only; `&&` outranks the badge's own single-class rules.
      mobileBadge: {
        [theme.breakpoints.down('xs')]: {
          '&&': {
            height: 24,
            boxSizing: 'border-box',
            padding: '0 8px 0 12px',
            borderRadius: 6,
            borderWidth: 0.8,
            lineHeight: 'normal'
          }
        }
      },
      mobileBadgeIcon: {
        [theme.breakpoints.down('xs')]: {
          '&&': {
            fontSize: '14px !important'
          }
        }
      },
      mobileMetaDot: {
        width: 4,
        height: 4,
        borderRadius: '50%',
        backgroundColor: 'currentColor',
        flexShrink: 0
      },
      statusCell: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        [theme.breakpoints.down('xs')]: {
          gridArea: 'status'
        }
      },
      refundArrow: {
        color: neutral.P75,
        fontSize: 18
      },
      // Amount cell: bold amount with the (muted) stage label inline after it (e.g. "$100.00 Deposit").
      // `overflow: hidden` keeps both halves inside the track — without it the two nowrap children
      // spill into the Status column and the badge paints over the tail of the stage label.
      amountCell: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        minWidth: 0,
        overflow: 'hidden',
        [theme.breakpoints.down('xs')]: {
          gridArea: 'amount'
        }
      },
      // The price never truncates — the stage label gives up its width first (see `stage`).
      // Mobile takes the prototype's 14px / 400 / #354962 (between Neutral P40 and P35, no token);
      // an unpaid amount still greys out, since `nonSummedAmount` comes later in the sheet.
      amount: {
        whiteSpace: 'nowrap',
        flexShrink: 0,
        [theme.breakpoints.down('xs')]: {
          fontSize: theme.typography.pxToRem(14),
          fontWeight: 400,
          lineHeight: 'normal',
          color: '#354962'
        }
      },
      nonSummedAmount: {
        color: neutral.P75,
        '& span': {
          color: neutral.P75
        }
      },
      stage: {
        color: neutral.P75,
        fontSize: theme.typography.pxToRem(13),
        whiteSpace: 'nowrap',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      },
      settingsButton: {
        textTransform: 'none',
        color: neutral.P60,
        fontWeight: 500,
        // The prototype's mobile Settings pill: 28px, 8px in, 6px corners, 16px icon, 12px / 500 /
        // #55647c, and a #e8ecf0 wash on hover. `&&` outranks the shared Button's own rules.
        [theme.breakpoints.down('xs')]: {
          '&&': {
            height: 28,
            minHeight: 28,
            minWidth: 0,
            padding: theme.spacing(0, 1),
            borderRadius: 6,
            fontSize: theme.typography.pxToRem(12),
            fontWeight: 500,
            lineHeight: 1.4,
            color: '#55647c',
            '&:hover': {
              backgroundColor: '#e8ecf0'
            },
            '& svg': {
              fontSize: 16
            }
          }
        }
      },
      kebabCell: {
        display: 'flex',
        justifyContent: 'flex-end',
        [theme.breakpoints.down('xs')]: {
          display: 'none'
        },
        '@media print': {
          display: 'none'
        }
      },
      // Detached, fully dashed-bordered add box below the card (matches the item tables' add button).
      addButton: {
        width: '100%',
        justifyContent: 'center',
        border: `1px dashed ${neutral.P85} !important`,
        borderRadius: `${theme.borderRadius.lg} !important`,
        backgroundColor: `${neutral.White} !important`,
        color: theme.palette.primary.main,
        fontWeight: 600,
        minHeight: 52,
        '&:hover': {
          backgroundColor: `${neutral.P98} !important`
        }
      }
    }
  },
  { name: 'PaymentsTableNext' }
)

/**
 * The "Payments" section for the new (estimateNext) pipeline, rendered in the outlined style. Reuses
 * `usePaymentActions` (add/request/collect/edit/delete + eligibility) and the same row action set as
 * the classic `PaymentsTable`, but lays the table out as an outlined card: a white title strip with
 * a Hide pill, a light column-header band (Status / Date / Method / Note), aligned data rows with a
 * right-aligned amount + kebab, and an inset "Add Payment" footer.
 */
export default function PaymentsTableNext({ inPresentation, collapsed, onChange }: PaymentsTableNextProps) {
  const classes = useStyles()
  const { quote, isEditable, save: saveQuote, isDirty, onEditSettings } = useQuote()
  const { openDialog, dismissDialog } = useContext(DialogStackContext)
  const { options, clientId } = useClientOptions()
  const { user, preferences, isPainter } = useUser()
  const { paymentImprovements } = useFlags()
  const { hasPaymentSettings } = useHasPaymentSettings(quote)

  const {
    payments,
    displayPayments,
    achLabel,
    isPaymentEligible,
    canCollectCCPayment,
    canCollectACHPayment,
    deletePayment,
    handleEditPayment,
    handleCollectPayment,
    handleAddRequestPayment,
    checkingForNewPayments
  } = usePaymentActions({ inPresentation, onChange })

  if (collapsed && !isEditable) {
    return null
  }

  // Opens the consolidated Estimate Settings dialog on its Payments tab (wired via the shared quote
  // context, since this table lives in the react-quote package and can't import the app dialog).
  const handleSettings = () => {
    onEditSettings?.('payments')
  }

  async function handleDownloadPDF(index: number) {
    const payment = payments[index]
    if (isDirty) {
      await saveQuote()
    }
    openDialog(QuotePdfDialog, {
      quote,
      companyId: clientId,
      options,
      silent: true,
      view: 'payment-receipt',
      paymentId: payment.id,
      onConfirm: dismissDialog
    })
  }

  async function handleActionClick(actionKey: string, index: number) {
    const allPayments = getPayments({ quote })
    const payment = allPayments[index]
    switch (actionKey) {
      case 'delete':
        deletePayment(index)
        break
      case 'edit':
        handleEditPayment({ paymentIndex: index })
        break
      case 'refund':
        openDialog(RefundPaymentDialog, { payment, allPayments, onClose: dismissDialog })
        break
      case 'download':
        handleDownloadPDF(index)
        break
      case 'resend':
        openDialog(SendPaymentRequestDialog, { payment: allPayments[index], user, preferences, isResend: true })
        break
      case 'mark-as-paid':
        openDialog(TileListDialog, {
          title: 'Mark as Successful',
          showTopClose: true,
          items: [
            { key: RECORD_PAYMENT_KEY, label: 'Record Payment' },
            canCollectCCPayment && { key: 'credit', label: 'Take Credit Card Payment' },
            canCollectACHPayment && { key: 'ach', label: `Take ${achLabel} Payment` }
          ].filter(Boolean),
          onItemSelect: (key: string) => {
            if (key === RECORD_PAYMENT_KEY) {
              handleEditPayment({ paymentIndex: index, backOnCancel: true, markingPaid: true })
            } else {
              handleCollectPayment({
                amount: payment.amount,
                note: payment.note,
                deposit: payment.deposit,
                paymentRequestId: payment.id,
                paymentType: key as PayrixPaymentType
              })
            }
          }
        })
        break
      case 'view-in-portal': {
        const portalLink = getPaymentPortalLink(payment.providerDetails, options)
        if (portalLink) {
          window.open(portalLink, '_blank', 'noopener,noreferrer')
        }
        break
      }
    }
  }

  const getActions = (index: number) => {
    const payment = payments[index]
    const isRequestWithProcessingPayment = requestHasProcessingPayment({ quote, payment })
    const showResend = !!payments[index] && payment.status === 'pending' && !(payment.amount > quote.totals.balance_due)
    const isProcessedPayment = !!payment?.activityId && !payment?.manual

    return [
      !isRequestWithProcessingPayment && showResend && { key: 'resend', icon: SendIcon, label: 'Resend' },
      !isRequestWithProcessingPayment && payment.status === 'pending' && { key: 'edit', icon: EditIcon, label: 'Edit' },
      !isRequestWithProcessingPayment &&
        payment.status === 'pending' && { key: 'mark-as-paid', icon: CheckIcon, label: 'Mark as Successful' },
      paymentImprovements && canRefundPayment(payment, payments) && { key: 'refund', icon: UndoIcon, label: 'Refund' },
      !isPainter &&
        ['paid', 'refunded', 'failed'].includes(payment.status) && {
          key: 'download',
          icon: DownloadIcon,
          label: 'Download Receipt'
        },
      isProcessedPayment &&
        (payment.providerDetails?.payrix?.txnId ?? payment.providerDetails?.stripe?.paymentIntentId) && {
          key: 'view-in-portal',
          icon: payment.providerDetails?.stripe?.paymentIntentId ? StripeMark : LaunchIcon,
          label: payment.providerDetails?.stripe?.paymentIntentId ? 'View in Stripe' : 'View in Portal'
        },
      !isRequestWithProcessingPayment && !isProcessedPayment && { key: 'delete', icon: DeleteIcon, label: 'Delete' }
    ].filter(Boolean)
  }

  return (
    <div className={classes.root} id="payments">
      <div className={classes.card}>
        <div
          className={classnames(classes.titleHeader, {
            [classes.titleHeaderFlush]: collapsed || displayPayments.length === 0
          })}
        >
          <Typography component="span" className={classes.title}>
            Payments
          </Typography>
          {/* No Payments tab to open (manual payments only), so no button (ENG-1997). */}
          {isEditable && hasPaymentSettings && (
            <Button
              variant="text"
              size="small"
              icon={SettingsIcon}
              className={classes.settingsButton}
              onClick={handleSettings}
            >
              Settings
            </Button>
          )}
        </div>

        <Collapse show={!collapsed}>
          {displayPayments.length > 0 && (
            <div className={classes.colHeader}>
              <span className={classes.colLabel}>Amount</span>
              <span className={classes.colLabel}>Status</span>
              <span className={classnames(classes.colLabel, classes.hideOnMobile)}>Method</span>
              <span className={classnames(classes.colLabel, classes.hideOnMobile)}>
                {inPresentation ? 'Note' : 'Client Note'}
              </span>
              <span className={classes.colLabel}>Date</span>
              <span />
            </div>
          )}

          <div>
            {displayPayments.map((payment, displayIndex) => {
              const originalIndex = payments.findIndex((p) => p.id === payment.id)
              const isChildRow =
                !!payment.forPaymentId ||
                (!inPresentation &&
                  !!payment.paymentRequestId &&
                  (payment.status === 'processing' || payment.status === 'failed'))
              const isRefund = !!payment.forPaymentId || payment.status === 'refunded'
              const displayAmount = isRefund ? Math.abs(payment.amount) : payment.amount
              const paymentStage = getPaymentStage({ payment, quote })
              const actions = getActions(originalIndex)

              return (
                <div
                  key={payment.id ?? displayIndex}
                  className={classes.dataRow}
                  onClick={() => handleEditPayment({ paymentIndex: originalIndex })}
                >
                  <span className={classes.amountCell}>
                    {isChildRow && <SubdirectoryArrowRightIcon className={classes.refundArrow} />}
                    <Typography
                      component="span"
                      className={classnames(classes.amount, {
                        [classes.nonSummedAmount]: !['paid', 'refunded'].includes(payment.status)
                      })}
                      value={displayAmount}
                      format={'price'}
                      showUnits
                      variant={'body1'}
                    />
                    {paymentStage && (
                      <span className={classnames(classes.stage, classes.hideOnMobile)}>{paymentStage}</span>
                    )}
                  </span>
                  <span className={classes.statusCell}>
                    <PaymentStatusBadgeWithTooltip
                      status={payment.status}
                      amount={payment.amount}
                      type={payment.type}
                      balanceDue={quote.totals.balance_due}
                      achLabel={achLabel}
                      failedEvents={payment.failedEvents}
                      inPresentation={inPresentation}
                      badgeClasses={{ root: classes.mobileBadge, icon: classes.mobileBadgeIcon }}
                    />
                  </span>
                  <span className={classnames(classes.cell, classes.hideOnMobile)}>{payment.type}</span>
                  <span className={classnames(classes.cell, classes.hideOnMobile)}>{payment.note}</span>
                  {/*
                    `disableWrapper` matters here: Tooltip otherwise wraps children in a MUI Badge
                    whose inline-flex root becomes the grid item, so it sizes to the date's content
                    and `cell`'s ellipsis never applies — the date then runs under the kebab.
                    The short month keeps it inside the track; the tooltip carries the full date.
                  */}
                  <Tooltip hideIcon disableWrapper content={moment(payment.date).format('MMMM D, YYYY, h:mm a')}>
                    <span className={classnames(classes.cell, classes.hideOnMobile)}>
                      {moment(payment.date).format('MMM D, YYYY')}
                    </span>
                  </Tooltip>
                  <span className={classes.mobileMeta}>
                    {paymentStage && (
                      <>
                        <span>{paymentStage}</span>
                        <span className={classes.mobileMetaDot} aria-hidden />
                      </>
                    )}
                    <span>{moment(payment.date).format('MMM D, YYYY')}</span>
                  </span>
                  <span className={`${classes.kebabCell} allow-propagation`}>
                    {!inPresentation && (
                      <ActionButton
                        className="allow-propagation"
                        actions={actions}
                        onActionClick={(_ev, actionKey) => handleActionClick(actionKey, originalIndex)}
                        disabled={actions.length === 0}
                      />
                    )}
                  </span>
                </div>
              )
            })}
          </div>
        </Collapse>
      </div>

      {!inPresentation && !collapsed && (
        <AddItemButton
          id="allow-propagation"
          className={classes.addButton}
          loading={checkingForNewPayments}
          onClick={handleAddRequestPayment}
        >
          {`${isPaymentEligible ? 'Add/Request' : 'Add'} Payment`}
        </AddItemButton>
      )}
    </div>
  )
}
